import { CSSProperties, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Timeout } from "node";

import { useSaveLiveTagValue } from "@/components/Live/API/LiveMutations";
import {
	alertButtonStyle,
	alertTagStyle,
	defaultButtonStyle,
	defaultTagStyle,
	isStepActive,
	successButtonStyle,
	successTagStyle,
	waitButtonStyle,
	waitTagStyle,
} from "@/components/Procedures/Checklist/utils/Helpers";

import { ProcedureListenerTag } from "@/model/Procedure/ProcedureListenerTag";
import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { ProcedureNodeStepStatusTypes } from "@/model/Procedure/ProcedureNodeStepStatus";
import { UpdateProcedureDetailStepStatusRequest } from "@/model/Procedure/UpdateProcedureDetailStepStatusRequest";
import { OmniaDatapoints } from "@/model/SATOSLive/Omnia/OmniaDatapointResponse";
import { SavedLiveTagRequest } from "@/model/SATOSLive/Settings/SavedTagValue";

import { UpdateProcedureDetailStepState } from "@/service/ProcedureMutations";
import { useFetchActiveChecklist } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

export type ButtonStyling = { button: CSSProperties };

export interface StepStylingStatus {
	buttonStyling?: { button: CSSProperties };
	liveTagStatuses: Statuses[];
}

export type LiveTagStyling = {
	container: React.CSSProperties;
	pulseAnimation?: React.CSSProperties;
};

export interface Statuses {
	tag: string;
	status: boolean;
	value: number | undefined;
	styling: {
		container: React.CSSProperties;
		pulseAnimation?: React.CSSProperties;
	};
}

const useValueWithBoundaries = (
	currentStep: ProcedureNodeStep,
	activeSchemeId: number,
) => {
	const { id } = useParams<RouteParams>();
	const [values, setValues] = useState({});
	const [approvedTimestamp, setApprovedTimestamp] = useState(null);
	const [error, setError] = useState<string | null>(null);
	const [color, setColor] = useState<string | null>("grey");
	const [buttonStyling, setButtonStyling] = useState<ButtonStyling>({
		button: defaultButtonStyle.button,
	});
	const [stepIsActive, setStepIsActive] = useState<boolean>(false);
	const [liveTagStatuses, setLiveTagStatuses] = useState<{
		[key: string]: LiveTagStyling;
	}>({});
	const [stepStatuses, setStepStatuses] = useState<StepStylingStatus>({
		buttonStyling: defaultButtonStyle,
		liveTagStatuses: [],
	});
	// TODO: Convert this
	const { isLoading, data: activeNodeSteps } = useFetchActiveChecklist(
		activeSchemeId,
		0,
		"",
	);

	const SaveValue = useSaveLiveTagValue();
	const updateProcedureDetailStepState = UpdateProcedureDetailStepState();

	const fetchData = async () => {
		if (currentStep.IsOverridden || currentStep.Status) {
			return;
		}

		const tagNames = currentStep.ListenerTags.map((listenerTag) => {
			if (listenerTag.SavedTagValue !== null || currentStep.IsOverridden) {
				updateStepSuccess(listenerTag.Tag);
				return null; // Skip iteration
			} else {
				updateStepWait(listenerTag.Tag);
				return { TagName: listenerTag.Tag };
			}
		}).filter((tagName) => tagName !== null);

		if (tagNames.length === 0) {
			return;
		}

		try {
			//Make API call to fetch value based on ID
			const response = await fetch(`/api/omnia/datapoints/multiple/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(tagNames),
			});
			const fetchedValue: OmniaDatapoints[] = await response.json();

			fetchedValue.forEach(async (datapoint) => {
				if (!datapoint || !datapoint.datapoints || !datapoint.datapoints[0]) {
					return;
				}
				setValues((prevState) => ({
					...prevState,
					[datapoint.name]: datapoint.datapoints[0].value,
				}));
				const { Min, Max, Tag, Id } = currentStep.ListenerTags.find(
					(listenerTag) => listenerTag?.Tag === datapoint.name,
				) as ProcedureListenerTag;
				const value = datapoint.datapoints[0].value;
				if (
					!Number.isNaN(value) &&
					value !== undefined &&
					value >= Min &&
					value <= Max
				) {
					try {
						const request: SavedLiveTagRequest = {
							StepId: currentStep.Id,
							ProcedureId: +id,
							SchemeId: activeSchemeId,
							SavedTagValue: value,
							Tag: datapoint.name,
							TagId: Id,
						};
						await SaveValue(request);
						setColor("green");
						updateStepSuccess(Tag);
					} catch (error) {
						setColor("red");
						updateStepAlert(Tag);
						setError("Error saving data");
					}
				}
			});
		} catch (error: any) {
			setError(error.message);
			setColor("red");
			tagNames.forEach((tagName) => updateStepAlert(tagName?.TagName));
			return;
		}
	};

	const updateStepWait = (tagName: string) => {
		setButtonStyling(waitButtonStyle);
		setLiveTagStatuses((prevState) => {
			return {
				...prevState,
				[tagName]: waitTagStyle,
			};
		});
	};

	const updateStepAlert = (tagName: string) => {
		setButtonStyling(alertButtonStyle);
		setLiveTagStatuses((prevState) => {
			return {
				...prevState,
				[tagName]: alertTagStyle,
			};
		});
	};

	const updateStepSuccess = (tagName: string) => {
		setLiveTagStatuses((prevState) => {
			return {
				...prevState,
				[tagName]: successTagStyle,
			};
		});
	};

	const updateStepDefault = (tagName: string) => {
		setButtonStyling(defaultButtonStyle);
		setLiveTagStatuses((prevState) => {
			return {
				...prevState,
				[tagName]: defaultTagStyle,
			};
		});
	};

	const updateStepStatus = async (status: ProcedureNodeStepStatusTypes) => {
		try {
			const request: UpdateProcedureDetailStepStatusRequest = {
				ArchiveType: "Status",
				StepId: currentStep.Id,
				ProcedureId: parseInt(id),
				SchemeId: activeSchemeId,
				File: null,
				Status: status,
				Metadata: { ...currentStep, Status: status },
			};
			await updateProcedureDetailStepState(request);
		} catch (error) {
			setError("Error updating step status");
		}
	};

	useEffect(() => {
		let interval: Timeout;
		if (currentStep.ChecklistType === "Listener") {
			if (currentStep.IsSkipped) {
				currentStep.ListenerTags.forEach((listenerTag) =>
					updateStepDefault(listenerTag.Tag),
				);
				setStepIsActive(false);
				clearInterval(interval);
				return;
			}

			if (currentStep.Status) {
				currentStep.ListenerTags.forEach((listenerTag) =>
					updateStepSuccess(listenerTag.Tag),
				);
				setStepIsActive(false);
				clearInterval(interval);
				return;
			}

			if (isStepActive(currentStep, activeNodeSteps) && !currentStep.Status) {
				setStepIsActive(true);
				// fetchData();
				interval = setInterval(fetchData, 1000);
			}
		}

		// Cleanup function if needed
		return () => {
			clearInterval(interval);
		};
	}, [activeNodeSteps, currentStep.Status]);

	useMemo(() => {
		if (currentStep.IsSkipped) {
			currentStep.ListenerTags.forEach((listenerTag) =>
				updateStepDefault(listenerTag.Tag),
			);
			setStepIsActive(false);
			return;
		}

		if (currentStep.Status) {
			currentStep.ListenerTags.forEach((listenerTag) =>
				updateStepSuccess(listenerTag.Tag),
			);
			setButtonStyling(successButtonStyle);
			setStepIsActive(false);
			return;
		}
		//
		if (
			currentStep.ListenerTags.every(
				(listenerTag) => listenerTag.SavedTagValue !== null,
			) &&
			currentStep.ChecklistType === "Listener"
		) {
			!currentStep.Status && updateStepStatus(1);
			currentStep.ListenerTags.forEach((listenerTag) =>
				updateStepSuccess(listenerTag.Tag),
			);
			setButtonStyling(successButtonStyle);
			setStepIsActive(false);
			return;
		}
	}, [currentStep.ListenerTags, activeNodeSteps, currentStep.Status]);

	return {
		values,
		error,
		color,
		stepStatuses: { buttonStyling, liveTagStatuses },
	};
};

export default useValueWithBoundaries;
